import { useState, useEffect, useCallback, useMemo } from "react";
import {
  useAuthInfo,
  useRedirectFunctions,
  saveOrgSelectionToLocalStorage,
  loadOrgSelectionFromLocalStorage,
} from "@propelauth/react";
import type { OrgMemberInfo } from "@propelauth/javascript";

export const ACTIVE_ORG_STORAGE_KEY = "__last_selected_org";

export interface UseOrgResult {
  orgs: OrgMemberInfo[];
  activeOrg: OrgMemberInfo | null;
  activeOrgId: string | null;
  activeOrgName: string | null;
  activeOrgRole: string | null;
  isActiveOrgAdmin: boolean;
  loading: boolean;
  hasOrgs: boolean;
  switchOrg: (orgIdOrName: string) => void;
  redirectToOrgPage: (orgId?: string) => void;
  redirectToCreateOrgPage: () => void;
}

export function useOrg(): UseOrgResult {
  const authInfo = useAuthInfo();
  const { redirectToOrgPage, redirectToCreateOrgPage } = useRedirectFunctions();

  const orgHelper = authInfo.loading || !authInfo.isLoggedIn ? null : authInfo.orgHelper;

  const orgs: OrgMemberInfo[] = useMemo(() => {
    if (!orgHelper) return [];
    return orgHelper.getOrgs() || [];
  }, [orgHelper]);

  const [selectedOrgIdOrName, setSelectedOrgIdOrName] = useState<string | null>(() => {
    try {
      return loadOrgSelectionFromLocalStorage() || localStorage.getItem(ACTIVE_ORG_STORAGE_KEY);
    } catch {
      return null;
    }
  });

  const activeOrg: OrgMemberInfo | null = useMemo(() => {
    if (!orgHelper || orgs.length === 0) return null;

    if (selectedOrgIdOrName) {
      const matched =
        orgHelper.getOrg(selectedOrgIdOrName) || orgHelper.getOrgByName(selectedOrgIdOrName);
      if (matched) return matched;
    }

    return orgs[0] ?? null;
  }, [orgHelper, orgs, selectedOrgIdOrName]);

  useEffect(() => {
    if (activeOrg) {
      if (selectedOrgIdOrName !== activeOrg.orgId) {
        setSelectedOrgIdOrName(activeOrg.orgId);
        try {
          saveOrgSelectionToLocalStorage(activeOrg.orgId);
        } catch {
          // ignore storage errors
        }
      }
    } else if (orgs.length === 0 && selectedOrgIdOrName !== null) {
      setSelectedOrgIdOrName(null);
    }
  }, [activeOrg, orgs.length, selectedOrgIdOrName]);

  const switchOrg = useCallback(
    (orgIdOrName: string) => {
      if (!orgHelper) return;
      const target = orgHelper.getOrg(orgIdOrName) || orgHelper.getOrgByName(orgIdOrName);
      if (target) {
        setSelectedOrgIdOrName(target.orgId);
        try {
          saveOrgSelectionToLocalStorage(target.orgId);
        } catch {
          // ignore storage errors
        }
      }
    },
    [orgHelper]
  );

  const activeOrgRole = activeOrg?.userAssignedRole || null;
  const isActiveOrgAdmin =
    activeOrgRole === "Owner" ||
    activeOrgRole === "Admin" ||
    (Boolean(activeOrg) &&
      typeof (activeOrg as any).isRole === "function" &&
      ((activeOrg as any).isRole("Owner") || (activeOrg as any).isRole("Admin")));

  return {
    orgs,
    activeOrg,
    activeOrgId: activeOrg ? activeOrg.orgId : null,
    activeOrgName: activeOrg ? activeOrg.orgName : null,
    activeOrgRole,
    isActiveOrgAdmin,
    loading: authInfo.loading,
    hasOrgs: orgs.length > 0,
    switchOrg,
    redirectToOrgPage: (orgId?: string) => redirectToOrgPage(orgId || activeOrg?.orgId),
    redirectToCreateOrgPage,
  };
}
